import { RelapsePreventionPlacesByOptionId } from "../../../hooks/useRelapsePreventionPlaces"
import {
  Section,
  SelectedOption,
  SelectedSection,
} from "../../../utils/safetyPlanUtils"

/**
 * Builds the ordered option rows a section should render.
 *
 * Most rows come from `selected.selectedOptionsMap`, which mirrors the saved
 * safety-plan JSON. Relapse-prevention places are different: the actual places
 * now live in Postgres, so their risky/safe option ids will be missing from that
 * JSON even when places exist. This helper adds those missing template options
 * back in when fetched places are present, then returns one ordered array for
 * the view.
 */
export function buildRenderableSelectedOptions(
  selected: SelectedSection,
  section: Section,
  relapsePreventionPlacesByOptionId: RelapsePreventionPlacesByOptionId
): SelectedOption[] {
  const renderableOptions = [...selected.selectedOptionsMap.values()]
  const selectedOptionIds = new Set(
    renderableOptions.map((option) => option.id)
  )

  // The renderer still works in terms of option rows, so we add only the
  // missing risky/safe template options that have fetched places behind them.
  for (const category of section.optionsMap.values()) {
    const isRelapsePreventionPlaceCategory =
      category.category_type === "risky_place" ||
      category.category_type === "safe_place"

    if (!isRelapsePreventionPlaceCategory) {
      continue
    }

    for (const responseOption of category.response_options) {
      const hasFetchedPlaces =
        (relapsePreventionPlacesByOptionId.get(responseOption.id) ?? [])
          .length > 0

      if (!hasFetchedPlaces || selectedOptionIds.has(responseOption.id)) {
        continue
      }

      renderableOptions.push({
        id: responseOption.id,
        category_type: category.category_type,
        custom_values: [],
        display_order: responseOption.display_order,
        response_text: responseOption.response_text,
      })
      selectedOptionIds.add(responseOption.id)
    }
  }

  renderableOptions.sort(
    (left, right) => left.display_order - right.display_order
  )

  return renderableOptions
}
